import { gql } from '@apollo/client';

import type { ErrorMessageTypes } from '../components/alerts/errors/error-map';

import { SsoApiClient } from './sso-api-client';

export type AccountLockoutResponse = {
    success: boolean;
    failedAttempts?: number;
    remainingAttempts?: number;
    minutesUntilUnlock?: number;
    isLocked: boolean;
    error?: ErrorMessageTypes;
};

/**
 * Check if an account is locked due to too many verification attempts
 * @param phoneNumber - The phone number to check
 * @returns Object with lock status, failed attempts, remaining attempts, and minutes until unlock
 */
export const checkAccountLockoutApi = async (
    phoneNumber: string
): Promise<AccountLockoutResponse> => {
    try {
        const { data } = await SsoApiClient.query({
            query: gql`
                query CheckAccountLockout($phoneNumber: String!) {
                    lockoutStatus(phoneNumber: $phoneNumber)
                        @rest(
                            type: "LockoutStatus"
                            path: "/credentials/lockout-status/{args.phoneNumber}"
                        ) {
                        failedAttempts
                        remainingAttempts
                        minutesUntilUnlock
                    }
                }
            `,
            variables: { phoneNumber },
            fetchPolicy: 'network-only',
        });

        const lockoutData = data?.lockoutStatus;
        const minutesUntilUnlock = lockoutData?.minutesUntilUnlock ?? 0;
        const isLocked = minutesUntilUnlock > 0;

        return {
            success: true,
            failedAttempts: lockoutData?.failedAttempts ?? 0,
            remainingAttempts: lockoutData?.remainingAttempts ?? 0,
            minutesUntilUnlock,
            isLocked,
        };
    } catch {
        return {
            success: false,
            isLocked: false,
            error: '500_GenericError',
        };
    }
};
